import { useState, useEffect } from 'react';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../utils/api';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import styles from './AdminTable.module.css';

const EMPTY = { name: '', nameBn: '', icon: '', description: '', sortOrder: 0, isActive: true };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetch = () => getCategories().then(r => setCategories(r.data)).finally(() => setLoading(false));
  useEffect(() => { fetch(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditCat(null); setShowModal(true); };
  const openEdit = (c) => { setForm({ ...c }); setEditCat(c); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editCat) { await updateCategory(editCat._id, form); toast.success('Category updated!'); }
      else          { await createCategory(form);               toast.success('Category created!'); }
      setShowModal(false);
      fetch();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await deleteCategory(id); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed'); }
  };

  const f = (field) => (e) => setForm(p => ({
    ...p,
    [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Categories</h2>
          <p className={styles.pageSubtitle}>{categories.length} categories</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus size={16}/> Add Category</button>
      </div>

      <div className={styles.tableCard}>
        {loading ? <div className="spinner-center"><div className="spinner"/></div> : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Category</th><th>Bangla Name</th><th>Sort Order</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr><td colSpan={5} className={styles.emptyRow}>No categories yet</td></tr>
                ) : categories.map(c => (
                  <tr key={c._id}>
                    <td>
                      <div className={styles.productCell}>
                        <span style={{ fontSize:'1.5rem' }}>{c.icon || '📦'}</span>
                        <p className={styles.productName}>{c.name}</p>
                      </div>
                    </td>
                    <td style={{ fontFamily:'Hind Siliguri, sans-serif' }}>{c.nameBn || '—'}</td>
                    <td>{c.sortOrder}</td>
                    <td><span className={`badge ${c.isActive ? 'badge-success' : 'badge-error'}`}>{c.isActive ? 'Active' : 'Hidden'}</span></td>
                    <td>
                      <div className={styles.actionBtns}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}><FiEdit2 size={14}/></button>
                        <button className="btn btn-ghost btn-sm" style={{ color:'var(--error)' }} onClick={() => handleDelete(c._id, c.name)}><FiTrash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} style={{ maxWidth:480 }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editCat ? 'Edit Category' : 'Add Category'}</h3>
              <button className={styles.modalClose} onClick={() => setShowModal(false)}><FiX size={20}/></button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalForm}>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
                <div className="form-group">
                  <label className="form-label">Category Name (English) *</label>
                  <input className="form-input" value={form.name} onChange={f('name')} required placeholder="e.g. Electronics"/>
                </div>
                <div className="form-group">
                  <label className="form-label">বাংলা নাম</label>
                  <input className="form-input" value={form.nameBn} onChange={f('nameBn')} placeholder="e.g. ইলেকট্রনিক্স" style={{ fontFamily:'Hind Siliguri, sans-serif' }}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Icon (Emoji)</label>
                  <input className="form-input" value={form.icon} onChange={f('icon')} placeholder="📱" maxLength={4}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-input" rows={2} value={form.description} onChange={f('description')} placeholder="Short description..."/>
                </div>
                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input type="number" className="form-input" value={form.sortOrder} onChange={f('sortOrder')} min={0}/>
                </div>
                <label className={styles.checkbox}>
                  <input type="checkbox" checked={form.isActive} onChange={f('isActive')}/> Active (visible on site)
                </label>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editCat ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
